import { useMutation, useQuery } from "@tanstack/react-query";
import {
	CheckCircle2,
	CheckSquare,
	FileUp,
	Info,
	Loader2,
	Search,
	Square,
	Store,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
	attemptDynamicImportRecovery,
	describeDynamicImportError,
	loadXlsx,
} from "@/lib/dynamic-import";
import { cn } from "@/lib/utils";
import {
	buildImportSummaryToast,
	extractSingleColumnFromSheet,
	extractSingleColumnFromText,
	type ImportReconciliation,
	ImportReportPanel,
	KSID_FORMAT,
	reconcileCodeImport,
	type SingleColumnExtraction,
} from "./code-import-report";
import { contractsApi, type PharmacyItem } from "./contracts-api";

const EMPTY_SELECTION: string[] = [];
export const FRANCHISE_REGION_PREFIX = "O-FRANCZYZA";
export const isFranchiseRegion = (region?: string) =>
	region?.startsWith(FRANCHISE_REGION_PREFIX) ?? false;

interface PharmacySelectorModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelect: (pharmacies: PharmacyItem[]) => void;
	initialSelection?: string[]; // unitcodes
	title?: string;
}

export function PharmacySelectorModal({
	open,
	onOpenChange,
	onSelect,
	initialSelection,
	title = "Wybierz apteki",
}: PharmacySelectorModalProps) {
	const selectedFromParent = initialSelection ?? EMPTY_SELECTION;
	const [searchQuery, setSearchQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [showSelectedOnly, setShowSelectedOnly] = useState(false);
	const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set());
	// Podsumowanie ostatniego importu z pliku — toast znika, a użytkownik musi
	// móc odczytać i skopiować kody, które nie weszły do zaznaczenia.
	const [importReport, setImportReport] = useState<ImportReconciliation | null>(null);

	// Map of unitcode -> PharmacyItem
	const [selectedPharmacies, setSelectedPharmacies] = useState<Map<string, PharmacyItem>>(
		new Map(),
	);

	// Synchronize selected pharmacies with parent state on each open.
	useEffect(() => {
		if (!open) return;
		if (selectedFromParent.length === 0) {
			setSelectedPharmacies(new Map());
			return;
		}

		contractsApi.searchByUnitCodes(selectedFromParent).then((data) => {
			const next = new Map<string, PharmacyItem>();
			for (const pharmacy of data) {
				next.set(pharmacy.unitcode, pharmacy);
			}
			setSelectedPharmacies(next);
		});
	}, [open, selectedFromParent]);

	// Świeże otwarcie modala = świeży import; stare podsumowanie wprowadzałoby w błąd.
	useEffect(() => {
		if (!open) setImportReport(null);
	}, [open]);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Dictionaries
	const { data: regions = [] } = useQuery({
		queryKey: ["pharmacies", "regions"],
		queryFn: contractsApi.getRegions,
		staleTime: 1000 * 60 * 60, // 1h
		enabled: open,
	});

	// Main search query (returns up to 1000)
	const { data: searchResponse, isFetching } = useQuery({
		queryKey: ["pharmacies", "search", debouncedQuery, Array.from(selectedRegions).sort()],
		queryFn: () =>
			contractsApi.searchPharmacies({
				q: debouncedQuery || undefined,
				regions: selectedRegions.size > 0 ? Array.from(selectedRegions) : undefined,
				limit: 1000,
			}),
		enabled: open,
	});

	const pharmacies = searchResponse?.items ?? [];

	useEffect(() => {
		if (selectedRegions.size === 0 || pharmacies.length === 0) return;

		setSelectedPharmacies((prev) => {
			const next = new Map(prev);
			let changed = false;
			for (const pharmacy of pharmacies) {
				if (!pharmacy.region || !selectedRegions.has(pharmacy.region)) continue;
				if (!next.has(pharmacy.unitcode)) {
					next.set(pharmacy.unitcode, pharmacy);
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [pharmacies, selectedRegions]);

	// Client-side filtering for multi-region support
	const filteredPharmacies = pharmacies.filter((p) => {
		if (selectedRegions.size === 0) return true;
		return p.region && selectedRegions.has(p.region);
	});
	const displayedPharmacies = showSelectedOnly
		? filteredPharmacies.filter((p) => selectedPharmacies.has(p.unitcode))
		: filteredPharmacies;

	// Search by UnitCode list (Import)
	const importMutation = useMutation({
		mutationFn: contractsApi.searchByUnitCodes,
		onSuccess: (data) => {
			const newMap = new Map(selectedPharmacies);
			for (const p of data) {
				newMap.set(p.unitcode, p);
			}
			setSelectedPharmacies(newMap);
		},
	});

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target;
		const file = input.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				const data = ev.target?.result;
				let extracted: SingleColumnExtraction;

				if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
					// Dynamic import xlsx — lazy chunk; loadXlsx centralises the
					// import target so a stale-chunk failure (Sentry 116556881)
					// surfaces as a translatable user message instead of an
					// unhandled rejection.
					const XLSX = await loadXlsx();
					const workbook = XLSX.read(data, { type: "binary" });
					const firstSheetName = workbook.SheetNames[0];
					const worksheet = workbook.Sheets[firstSheetName];
					const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
					extracted = extractSingleColumnFromSheet(jsonData, KSID_FORMAT);
				} else {
					const text =
						typeof data === "string" ? data : new TextDecoder().decode(data as ArrayBuffer);
					extracted = extractSingleColumnFromText(text, KSID_FORMAT);
				}

				// Wiele kolumn = nie zgadujemy, która trzyma KSID. Odrzucamy cały plik,
				// zamiast zaimportować "coś" z przypadkowej kolumny.
				if (extracted.columns > 1) {
					setImportReport(null);
					toast.error(`Import przyjmuje plik z jedną kolumną, a ten ma ich ${extracted.columns}.`, {
						description:
							"Zostaw w pliku wyłącznie kolumnę z KSID (5 lub 6 cyfr) i wczytaj ponownie.",
						duration: 10000,
					});
					return;
				}

				if (extracted.codes.length === 0) {
					// Kody o złym formacie i tak trzeba pokazać — inaczej plik z samymi
					// błędnymi wartościami wygląda jak plik bez kodów.
					if (extracted.invalid.length > 0) {
						setImportReport(reconcileCodeImport([], [], extracted.invalid));
					}
					toast.warning("Nie znaleziono w pliku żadnego kodu KSID (5 lub 6 cyfr).");
					return;
				}

				try {
					const uniqueCodes = Array.from(new Set(extracted.codes));
					const matched = await importMutation.mutateAsync(uniqueCodes);
					const report = reconcileCodeImport(
						extracted.codes,
						matched.map((p) => p.unitcode),
						extracted.invalid,
					);
					setImportReport(report);
					const summary = buildImportSummaryToast(report, report.duplicatesRemoved, KSID_FORMAT);
					const notify = summary.level === "warning" ? toast.warning : toast.success;
					notify(summary.message, {
						description: summary.description,
						duration: summary.level === "warning" ? 10000 : undefined,
					});
				} catch {
					toast.error("Nie udało się dopasować aptek z pliku. Spróbuj ponownie.");
				}
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				toast.error(describeDynamicImportError(err, "Błąd przy czytaniu pliku"));
			} finally {
				// Bez tego ponowny wybór TEGO SAMEGO pliku (np. po poprawieniu kodów)
				// nie wyzwala onChange i wygląda jak zawieszony przycisk.
				input.value = "";
			}
		};

		if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
			reader.readAsBinaryString(file);
		} else {
			reader.readAsText(file);
		}
	};

	const togglePharmacy = (pharmacy: PharmacyItem) => {
		const newMap = new Map(selectedPharmacies);
		if (newMap.has(pharmacy.unitcode)) {
			newMap.delete(pharmacy.unitcode);
		} else {
			newMap.set(pharmacy.unitcode, pharmacy);
		}
		setSelectedPharmacies(newMap);
	};

	const toggleRegion = (region: string) => {
		const newSet = new Set(selectedRegions);
		const newMap = new Map(selectedPharmacies);

		if (newSet.has(region)) {
			newSet.delete(region);
			for (const pharmacy of pharmacies) {
				if (pharmacy.region === region) {
					newMap.delete(pharmacy.unitcode);
				}
			}
		} else {
			newSet.add(region);
			for (const pharmacy of pharmacies) {
				if (pharmacy.region === region) {
					newMap.set(pharmacy.unitcode, pharmacy);
				}
			}
		}

		setSelectedRegions(newSet);
		setSelectedPharmacies(newMap);
	};

	const selectAllVisible = () => {
		const newMap = new Map(selectedPharmacies);
		const allVisibleSelected = displayedPharmacies.every((p) => newMap.has(p.unitcode));

		if (allVisibleSelected) {
			for (const p of displayedPharmacies) newMap.delete(p.unitcode);
		} else {
			for (const p of displayedPharmacies) newMap.set(p.unitcode, p);
		}
		setSelectedPharmacies(newMap);
	};

	const selectAllGlobal = () => {
		const newMap = new Map(selectedPharmacies);
		// Zaznaczamy wszystkie apteki z aktualnego zbioru danych (nie tylko widoczne po filtrze tabeli)
		for (const p of pharmacies) newMap.set(p.unitcode, p);
		// Zaznaczamy takze wszystkie grupy
		setSelectedRegions(new Set(regions));
		setSelectedPharmacies(newMap);
	};

	const selectOwn = () => {
		const newMap = new Map(selectedPharmacies);
		for (const p of pharmacies) {
			if (p.wlasna) newMap.set(p.unitcode, p);
		}
		setSelectedPharmacies(newMap);

		// Zaznaczamy takze grupy zaczynajace sie od W-
		const newRegions = new Set(selectedRegions);
		for (const r of regions) {
			if (r.startsWith("W-")) {
				newRegions.add(r);
			}
		}
		setSelectedRegions(newRegions);
	};

	const selectFranchise = () => {
		const newMap = new Map(selectedPharmacies);
		for (const p of pharmacies) {
			if (isFranchiseRegion(p.region)) {
				newMap.set(p.unitcode, p);
			}
		}
		setSelectedPharmacies(newMap);

		const newRegions = new Set(selectedRegions);
		for (const r of regions) {
			if (isFranchiseRegion(r)) {
				newRegions.add(r);
			}
		}
		setSelectedRegions(newRegions);
	};

	const handleConfirm = () => {
		onSelect(Array.from(selectedPharmacies.values()));
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden max-md:h-[calc(100dvh-0.5rem)]">
				<DialogHeader className="p-4 border-b bg-muted/20">
					<div className="space-y-1">
						<DialogTitle className="flex items-center gap-2 text-xl font-bold max-md:text-base">
							<Store className="size-5 text-primary" />
							{title}
						</DialogTitle>
						<DialogDescription className="flex items-center gap-2">
							Wybierz apteki z listy lub zaimportuj z pliku.
						</DialogDescription>
					</div>
				</DialogHeader>

				<div className="flex-1 flex overflow-hidden max-md:min-h-0 max-md:flex-col">
					{/* Sidebar Region Selector */}
					<aside className="w-80 border-r bg-muted/10 flex flex-col overflow-hidden max-md:max-h-36 max-md:w-full max-md:shrink-0 max-md:border-r-0 max-md:border-b">
						<div className="p-4 border-b bg-muted/5 max-md:px-3 max-md:py-2">
							<div className="text-xs font-black uppercase text-muted-foreground tracking-widest mb-1 max-md:mb-0">
								Zaznacz apteki z grupy
							</div>
						</div>
						<div className="flex-1 overflow-y-auto p-2">
							<div className="space-y-0.5">
								{regions.map((r) => (
									<label
										key={r}
										className={cn(
											"flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-xs font-medium max-md:min-h-9",
											selectedRegions.has(r) ? "bg-primary/10 text-primary" : "hover:bg-muted",
										)}
									>
										<input
											type="checkbox"
											className="hidden"
											checked={selectedRegions.has(r)}
											onChange={() => toggleRegion(r)}
										/>
										{selectedRegions.has(r) ? (
											<CheckSquare className="size-3.5" />
										) : (
											<Square className="size-3.5 opacity-30" />
										)}
										<span className="truncate">{r}</span>
									</label>
								))}
							</div>
						</div>
					</aside>

					{/* Main Content Area */}
					<div className="flex-1 flex flex-col min-w-0 bg-background max-md:min-h-0">
						<div className="p-4 border-b flex items-center gap-4 max-md:flex-wrap max-md:gap-2 max-md:p-3">
							<div className="relative flex-1 max-w-md max-md:max-w-none max-md:basis-full">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
								<Input
									placeholder="Szukaj apteki (Nazwa, Kod, Adres)..."
									className="pl-10 h-10 bg-muted/20"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									autoFocus
								/>
							</div>
							<Button
								type="button"
								variant={showSelectedOnly ? "default" : "outline"}
								size="sm"
								className="h-10 shrink-0 gap-2 max-md:flex-1"
								onClick={() => setShowSelectedOnly((current) => !current)}
							>
								<CheckSquare className="size-4" />
								Pokaż zaznaczone apteki
							</Button>
							{isFetching && <Loader2 className="size-4 animate-spin text-primary" />}
						</div>

						{importReport && (
							<ImportReportPanel
								report={importReport}
								format={KSID_FORMAT}
								onDismiss={() => setImportReport(null)}
								className="m-3 mb-0 max-md:max-h-48 max-md:shrink-0 max-md:overflow-y-auto"
							/>
						)}

						<div className="flex-1 overflow-auto max-md:min-h-40">
							<table className="w-full border-collapse text-[11px]">
								<thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm border-b border-border shadow-sm">
									<tr className="text-muted-foreground font-bold uppercase tracking-wider">
										<th className="w-10 px-4 py-3 text-center">
											<input
												type="checkbox"
												className="size-4 accent-primary cursor-pointer"
												checked={
													displayedPharmacies.length > 0 &&
													displayedPharmacies.every((p) => selectedPharmacies.has(p.unitcode))
												}
												onChange={selectAllVisible}
											/>
										</th>
										<th className="px-4 py-3 text-left w-16">ID</th>
										<th className="px-4 py-3 text-left">Nazwa</th>
										<th className="px-4 py-3 text-left w-24 text-center">Automat</th>
										<th className="px-4 py-3 text-left w-24">KSID</th>
										<th className="px-4 py-3 text-left w-32 text-center">Pobieramy dane</th>
										<th className="px-4 py-3 text-left">Nazwa apteki</th>
										<th className="px-4 py-3 text-left">Grupa</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-border/40">
									{displayedPharmacies.map((p) => (
										<tr
											key={p.unitcode}
											className={cn(
												"hover:bg-muted/30 transition-colors cursor-pointer group",
												selectedPharmacies.has(p.unitcode) && "bg-primary/5",
											)}
											onClick={() => togglePharmacy(p)}
										>
											<td
												className="px-4 py-2.5 text-center"
												onClick={(e) => e.stopPropagation()}
												onKeyDown={(e) => e.stopPropagation()}
											>
												<input
													type="checkbox"
													className="size-4 accent-primary cursor-pointer"
													checked={selectedPharmacies.has(p.unitcode)}
													onChange={() => togglePharmacy(p)}
												/>
											</td>
											<td className="px-4 py-2.5 text-muted-foreground font-mono">{p.id}</td>
											<td className="px-4 py-2.5 font-bold text-foreground truncate max-w-[200px]">
												{p.unitname}
											</td>
											<td className="px-4 py-2.5 text-center">
												{p.robotfetchdata ? (
													<span className="text-primary font-bold">Automat</span>
												) : (
													<span className="text-muted-foreground opacity-50">—</span>
												)}
											</td>
											<td className="px-4 py-2.5 font-mono font-bold text-primary">{p.unitcode}</td>
											<td className="px-4 py-2.5 text-center font-bold">
												{p.active ? (
													<span className="text-status-success-text">TAK</span>
												) : (
													<span className="text-status-error-text">NIE</span>
												)}
											</td>
											<td className="px-4 py-2.5 text-muted-foreground truncate max-w-[250px]">
												{p.fullname || "—"}
											</td>
											<td className="px-4 py-2.5">
												<Badge className="text-[9px] font-bold uppercase tracking-tighter h-5">
													{p.region || "Brak danych"}
												</Badge>
											</td>
										</tr>
									))}
									{displayedPharmacies.length === 0 && !isFetching && (
										<tr>
											<td
												colSpan={8}
												className="py-20 text-center text-muted-foreground italic text-sm"
											>
												{showSelectedOnly
													? "Brak zaznaczonych aptek dla bieżących filtrów."
													: "Nie znaleziono aptek spełniających kryteria."}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{/* Action Buttons Bar */}
						<div className="p-3 border-t bg-muted/5 flex items-center gap-3 max-md:flex-wrap max-md:gap-2 max-md:[&_button]:h-9">
							<Button
								variant="outline"
								size="sm"
								className="h-8 bg-status-info/5 border-status-info/20 text-status-info-text hover:bg-status-info/10 gap-2"
								onClick={selectAllGlobal}
							>
								<CheckCircle2 className="size-4" />
								Zaznacz wszystkie
							</Button>

							<Button
								variant="outline"
								size="sm"
								className="h-8 bg-status-warning/5 border-status-warning/20 text-status-warning-text hover:bg-status-warning/10"
								onClick={selectOwn}
							>
								Zaznacz Własne
							</Button>

							<Button
								variant="outline"
								size="sm"
								className="h-8 bg-status-info/5 border-status-info/20 text-status-info-text hover:bg-status-info/10"
								onClick={selectFranchise}
							>
								Zaznacz Franczyza
							</Button>

							<div className="flex-1 max-md:hidden" />

							<div className="flex items-center">
								<Button
									variant="outline"
									size="sm"
									className="h-8 gap-2 relative bg-status-info/5 border-status-info/20 text-status-info-text hover:bg-status-info/10 rounded-r-none border-r-0"
									onClick={() => document.getElementById("pharmacy-upload")?.click()}
									disabled={importMutation.isPending}
								>
									{importMutation.isPending ? (
										<Loader2 className="size-4 animate-spin" />
									) : (
										<FileUp className="size-4" />
									)}
									Zaznacz z pliku
									<input
										id="pharmacy-upload"
										type="file"
										className="hidden"
										accept=".csv,.xls,.xlsx,.txt,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
										onChange={handleFileUpload}
									/>
								</Button>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<div className="flex h-8 items-center justify-center rounded-r-md border border-status-info/20 bg-status-info/5 px-2 text-status-info-text hover:bg-status-info/10 cursor-help transition-colors max-md:h-9">
												<Info className="size-4" />
											</div>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
											<p className="font-bold">Instrukcja importu aptek:</p>
											<ul className="list-disc pl-4 space-y-1">
												<li>
													Akceptowane pliki:{" "}
													<span className="font-mono">.xlsx, .xls, .csv, .txt</span>
												</li>
												<li>
													<span className="font-bold">Dokładnie jedna kolumna</span> z kodami — plik
													z większą liczbą kolumn zostanie odrzucony.
												</li>
												<li>
													Kod jednostki (KSID) to <span className="font-bold">5 albo 6 cyfr</span> —
													np. <span className="font-bold text-primary">932200</span> lub{" "}
													<span className="font-bold text-primary">70151</span>. Część aptek ma w
													bazie kod pięciocyfrowy i taki plik jest poprawny.
												</li>
												<li>
													Wszystko inne (4 cyfry, 7 cyfr, tekst) odrzucamy i wypisujemy w
													podsumowaniu — nic nie znika po cichu.
												</li>
												<li>Brany jest pierwszy arkusz; nagłówek kolumny może zostać.</li>
											</ul>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
						</div>
					</div>
				</div>

				<DialogFooter className="p-4 border-t bg-muted/20 sm:justify-between items-center">
					<div className="text-xs text-muted-foreground flex items-center gap-4 max-md:w-full max-md:justify-between">
						<div>
							Wybrano:{" "}
							<span className="font-bold text-foreground text-sm">{selectedPharmacies.size}</span>{" "}
							aptek
						</div>
						{selectedPharmacies.size > 0 && (
							<button
								type="button"
								onClick={() => {
									setSelectedPharmacies(new Map());
									setSelectedRegions(new Set());
								}}
								className="text-status-error-text hover:underline underline-offset-4 max-md:min-h-9"
							>
								Wyczyść wybór
							</button>
						)}
					</div>
					<div className="flex items-center gap-3 max-md:w-full max-md:gap-2 max-md:[&>button]:h-11 max-md:[&>button]:min-w-0 max-md:[&>button]:flex-1">
						<Button variant="ghost" onClick={() => onOpenChange(false)}>
							Anuluj
						</Button>
						<Button
							disabled={selectedPharmacies.size === 0}
							onClick={handleConfirm}
							className="gap-2 min-w-[140px] font-bold"
						>
							<CheckCircle2 className="size-4" />
							Zapisz zmiany
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
